// @flow
import React, {useEffect, useState} from 'react';
import {useHistory, useParams} from "react-router-dom";
import Header from "../Header";
import BackButton from "../../GUI_COMMON/COMPONENTS/BackButton";
import TextInput from "../../GUI_COMMON/COMPONENTS/TextInput";
import LIButton from "../../GUI_COMMON/COMPONENTS/LIButton";
import Popup from "../../GUI_COMMON/COMPONENTS/Popup";
import ArchiverClientApi from "../../API/archiverClientApi";
import ArchiverGuiUtils from "../../SUPPORT/ArchiverGuiUtils";
import LocationSelector from "../LocationSelector";
import LIUtils from "../../GUI_COMMON/SUPPORT/LIUtils";
import TextArea from "../../GUI_COMMON/COMPONENTS/TextArea";

const MagMoveAdminPage = () => {

    const [, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [curLocationDetails, setCurLocationDetails] = useState(getDefaultLocation());
    const [destLocationDetails, setDestLocationDetails] = useState(getDefaultLocation());
    const [notes, setNotes] = useState("");
    const [curMagDetails, setCurMagDetails] = useState(null);
    const [fullLocationsList, setFullLocationsList] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    
    const {magBC} = useParams();
    const [magBarcode, setMagBarcode] = useState(magBC?magBC:"");
    
    const history = useHistory();

    useEffect(() => {
        console.debug("refreshing locations list");
        getLocationsList();
    }, []);
    
    useEffect( () => {
        if(!magBarcodeLooksGood(magBarcode)) { // ignore
            return;
        }

        console.debug("getting mag details for "+magBarcode);
        ArchiverClientApi.getMagDetailsGivenBarcode(magBarcode)
        .then((magDetails) => {
            setCurMagDetails(magDetails);
            const [locationId,deptId] = 
                LIUtils.getLocationAndDeptFromSingleLocationString(magDetails?.magazine.storageLocation, fullLocationsList);
            setCurLocationDetails({
                locationId, deptId
            });
            setDestLocationDetails({
                locationId, deptId
            });
        })
        .catch( responseError => {
            ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError);
            setCurMagDetails(null);
        });
    },[magBarcode,fullLocationsList]);
    
    function getLocationsList() {
        setIsLoading(true);
        ArchiverClientApi.getLocations(true) // request all enabled locations
        .then( (locations) => {
            setFullLocationsList(locations??[]);
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
            setIsLoading(false);
        });
    }
    
    function getDefaultLocation() {
        return {
            locationId:-1,
            deptId:-1
        }
    }

    function onChange(e) {
        const thing = e.target.name;
        let value = e.target.value;
        
        const locationEditInfo = {...destLocationDetails};
        if(thing==="magBarcode") {
            setMagBarcode(value);
        }
        else if(thing==="toLocation-destLocation") { // value here is the id selected
            const locId = Number(value);
            if(isNaN(locId)) {
                locationEditInfo.locationId=-1;
                locationEditInfo.deptId=-1;   // reset
            }
            else {
                locationEditInfo.locationId=locId;
                locationEditInfo.deptId=-1;
            }
        }
        else if(thing==="toLocation-destDepartment") { // value here is the id selected
            locationEditInfo.deptId=Number(value);
        }
        else if(thing==="notes") {
            setNotes(value);
        }

        setDestLocationDetails(locationEditInfo);
        setIsDirty(true);
    }

    function checkForValid() {
        if(!magBarcodeLooksGood(magBarcode)) {
            return false;
        }
        if(curMagDetails===null) {
            return false;
        }
        if(destLocationDetails.locationId===-1 || destLocationDetails.deptId===-1) {
            return false;
        }
        if(notes!=="") { // we allow valid if the dest location is the same, but we have new notes
            return true;
        }
        if(destLocationDetails.locationId===curLocationDetails.locationId && destLocationDetails.deptId===curLocationDetails.deptId) {
            return false;
        }
        return true;
    }

    function magBarcodeLooksGood(magBC) {
        if(!magBC || magBC==="") {
            return false;
        }
        const re = new RegExp('^[0-9][0-9][a-zA-Z]*.[0-9]{4}.[0-9]$');
        if(!re.test(magBC)) { // not complete
            console.log("bad mag barcode "+magBC);
            return false;
        }
        return true; // good
    }

    function handleCancelClicked(e) {
        e.preventDefault();
        e.stopPropagation();

        confirmClearDirty();
    }

    function handleSaveClicked(e) {
        e.preventDefault();
        e.stopPropagation();

        if(!checkForValid()) {
            return;
        }

        setIsSaving(true);
        ArchiverClientApi.sendMoveMagRequest(magBarcode,destLocationDetails.deptId, notes)
        .then( (savedLocationInfo) => {
            Popup.showInfo(`Magazine '${magBarcode}' was successfully moved to '${savedLocationInfo}'`);
            setIsDirty(false);
            // clear out the stored maginfo?
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
            setIsSaving(false);
        });
    }

    function confirmClearDirty() {
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    resetLocationDetailsShown();
                });
        }
        else {
            resetLocationDetailsShown(); // just clear what's there
        }
    }

    function updateUrlLink(toThis) {
        history.replace(toThis);
    }

    function resetLocationDetailsShown() {
        setCurLocationDetails(getDefaultLocation());
        setDestLocationDetails(getDefaultLocation());
        setMagBarcode("");
        setNotes("");
        setIsDirty(false);
        updateUrlLink("/admin/mag_move"); // blank

        // focus the first input field
        const nameEl = document.getElementsByName("name")[0];
        if(nameEl) {
            nameEl.focus();
        }
    }
    
    function handleBackClicked() {
        history.goBack();
    }
    
    return (
        <div className="container">
            <Header hideAdmin/>
            <div className="li-header-row">
                <BackButton onClick={handleBackClicked}/>
                <h1 className="ml-4 d-inline-block align-middle">Magazine Move</h1>
            </div>
            <p>Use this page to move a single magazine from one location to another.  The magazine must already exist in the system.</p>
            <p className="li-border-round li-bg-warn-light p-1">* You should try not to move magazines to or from an off-premises location via this page.  To do that
            you should create a Box Transfer Order and transfer the entire box.
            Moving magazines to an off-premises location also updates and locks all of its samples from being tracked further by the system.</p>
            <hr/>
                <div className="row px-3 pb-3 li-bg-light">
                    <div className="d-flex align-items-center">
                        <TextInput onChange={onChange} label="Magazine Barcode" name="magBarcode" widthPels={600} oneRow placeholder="##<neumonic>.####.#"
                                value={magBarcode} showClearButton goodNess={(curMagDetails!==null && magBarcodeLooksGood(magBarcode))?'good':'bad'}
                                helpText="* this magazine needs to already exist in the system"/>
                        
                    </div>
                </div>
                {/*<hr/>*/}
                <div className="row">
                    <div className="col-6 mt-3">
                        <h3 className="text-center">FROM</h3>
                        <div className="li-border-round li-border-dark p-2">
                            <div className="row px-3 pb-3 li-bg-light">
                                <div className="col-12">
                                    <LocationSelector name="fromLocation" onChange={onChange} locationLabel="Location"
                                                      locationValue={curLocationDetails.locationId}
                                                      departmentValue={curLocationDetails.deptId}
                                                      onIsValid={checkForValid}
                                                      showContact={false}
                                                      showOffPremises={true}
                                                      readOnly
                                                      withDepartmentsOnly/>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 mt-3">
                        <h3 className="text-center">TO</h3>
                        <div className="li-border-round li-border-dark p-2">
                            <div className="row px-3 pb-3 li-bg-light">
                                <div className="col-12">
                                    <LocationSelector name="toLocation" onChange={onChange} locationLabel="Location"
                                                      locationValue={destLocationDetails.locationId}
                                                      departmentValue={destLocationDetails.deptId}
                                                      onIsValid={checkForValid}
                                                      showContact={false}
                                                      showOffPremises={true}
                                                      required={true}
                                                      withDepartmentsOnly/>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="col-12 mt-3">
                        <TextArea onChange={onChange} label="Notes" name="notes" placeholder="" value={notes} rows={3}/>
                    </div>
                </div>
            
            <div className="text-center my-5">
                <LIButton name="cancel" label={isDirty?"Cancel":"Clear"} bgClass="mr-4" primary onClick={handleCancelClicked}/>
                <LIButton bgClass="" primary onClick={handleSaveClicked} label="Move" showBusy={isSaving} disabled={!(isDirty && checkForValid())}/>
            </div>
        </div>
    );
}

MagMoveAdminPage.propTypes = {
};

export default MagMoveAdminPage;